import { Hand, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { sendJson } from "../../api/rest";
import type { Schedule } from "../../types/flows";
import {
	btn,
	describeCron,
	Empty,
	ErrorBanner,
	fmtDateTime,
	fmtRelative,
	input,
	Panel,
} from "./ui";

// The cron text is rendered next to every expression because "0 6 * * 1" is not
// a thing anyone reads correctly under time pressure, and a schedule that fires
// on the wrong day is discovered a week later.

const TIMEZONES = ["Europe/Copenhagen", "UTC"];

const Row = ({
	schedule,
	flowId,
	onChanged,
}: {
	schedule: Schedule;
	flowId: number;
	onChanged: () => void;
}) => {
	const [draft, setDraft] = useState(schedule);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const dirty =
		draft.cronExpression !== schedule.cronExpression ||
		draft.timezone !== schedule.timezone ||
		draft.enabled !== schedule.enabled ||
		draft.name !== schedule.name;

	const save = async () => {
		setBusy(true);
		setError(null);
		try {
			await sendJson(`/flows/${flowId}/schedules/${schedule.id}`, "PUT", {
				name: draft.name ?? undefined,
				cronExpression: draft.cronExpression,
				timezone: draft.timezone,
				enabled: draft.enabled,
			});
			onChanged();
		} catch (e) {
			setError(String(e));
		} finally {
			setBusy(false);
		}
	};

	const remove = async () => {
		setBusy(true);
		try {
			await sendJson(`/flows/${flowId}/schedules/${schedule.id}`, "DELETE");
			onChanged();
		} catch (e) {
			setError(String(e));
		} finally {
			setBusy(false);
		}
	};

	return (
		<div className="space-y-2 rounded-xl border border-slate-200 p-3">
			<div className="flex flex-wrap items-center gap-2">
				<input
					className={`${input} w-44`}
					placeholder="Name"
					value={draft.name ?? ""}
					onChange={(event) => setDraft({ ...draft, name: event.target.value })}
				/>
				<input
					className={`${input} w-40 font-mono`}
					value={draft.cronExpression}
					onChange={(event) =>
						setDraft({ ...draft, cronExpression: event.target.value })
					}
				/>
				<select
					className={`${input} w-48`}
					value={draft.timezone}
					onChange={(event) =>
						setDraft({ ...draft, timezone: event.target.value })
					}
				>
					{TIMEZONES.map((tz) => (
						<option key={tz} value={tz}>
							{tz}
						</option>
					))}
				</select>
				<label className="flex items-center gap-1.5 text-[12px] text-slate-600">
					<input
						type="checkbox"
						checked={draft.enabled}
						onChange={(event) =>
							setDraft({ ...draft, enabled: event.target.checked })
						}
						className="h-4 w-4 rounded border-slate-300 text-primary"
					/>
					Enabled
				</label>
				<div className="ml-auto flex items-center gap-1">
					{dirty && (
						<button
							type="button"
							disabled={busy}
							onClick={save}
							className={btn.primary}
						>
							Save
						</button>
					)}
					<button
						type="button"
						disabled={busy}
						onClick={remove}
						className="rounded-lg p-2 text-slate-400 hover:bg-rose-50 hover:text-danger"
						aria-label="Delete schedule"
					>
						<Trash2 className="h-4 w-4" />
					</button>
				</div>
			</div>
			<p className="text-[12px] text-slate-500">
				{describeCron(draft.cronExpression)} · {draft.timezone}
				{" · next "}
				{schedule.nextRunAt ? (
					<span title={fmtDateTime(schedule.nextRunAt)}>
						{fmtRelative(schedule.nextRunAt)}
					</span>
				) : (
					"—"
				)}
				{" · last fired "}
				{schedule.lastFiredAt ? fmtRelative(schedule.lastFiredAt) : "never"}
			</p>
			<ErrorBanner error={error} />
		</div>
	);
};

export const ScheduleTab = ({
	flowId,
	schedules,
	humanTriggerOnly,
	onChanged,
}: {
	flowId: number;
	schedules: Schedule[];
	humanTriggerOnly: boolean;
	onChanged: () => void;
}) => {
	const [adding, setAdding] = useState(false);
	const [expression, setExpression] = useState("0 6 * * *");
	const [timezone, setTimezone] = useState(TIMEZONES[0]);
	const [error, setError] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);

	if (humanTriggerOnly) {
		return (
			<Panel>
				<div className="flex items-start gap-3">
					<span className="rounded-xl bg-violet-50 p-2.5 text-violet-600">
						<Hand className="h-5 w-5" />
					</span>
					<div className="space-y-2 text-[13px] leading-relaxed text-slate-600">
						<p className="font-medium text-slate-800">
							This flow has no schedule.
						</p>
						<p>
							It reads the comparison candidate queue, and which pairs get
							written about is a judgement someone makes rather than a timer.
							Start it from the{" "}
							<Link
								to="/flows/comparisons"
								className="font-medium text-primary"
							>
								Comparisons page
							</Link>{" "}
							by selecting candidates and pressing “Create pages”.
						</p>
						<p className="text-[12px] text-slate-500">
							Publishing is refused while an enabled schedule exists on such a
							flow, so there is nothing to configure here.
						</p>
					</div>
				</div>
			</Panel>
		);
	}

	const add = async () => {
		setBusy(true);
		setError(null);
		try {
			const result = await sendJson<{ id?: number; error?: string }>(
				`/flows/${flowId}/schedules`,
				"POST",
				{ cronExpression: expression, timezone },
			);
			if (result.error) setError(result.error);
			else {
				setAdding(false);
				onChanged();
			}
		} catch (e) {
			setError(String(e));
		} finally {
			setBusy(false);
		}
	};

	return (
		<Panel
			title="Schedules"
			hint="a flow may have several, each with its own parameters"
			action={
				<button
					type="button"
					onClick={() => setAdding(true)}
					className={btn.ghost}
				>
					<Plus className="h-4 w-4" /> Add schedule
				</button>
			}
		>
			<div className="space-y-3">
				{schedules.length === 0 && !adding && (
					<Empty>
						No schedules. This flow only runs when someone presses Run now.
					</Empty>
				)}

				{schedules.map((schedule) => (
					<Row
						key={schedule.id}
						schedule={schedule}
						flowId={flowId}
						onChanged={onChanged}
					/>
				))}

				{adding && (
					<div className="space-y-2 rounded-xl border border-primary/30 bg-primary/5 p-3">
						<div className="flex flex-wrap items-center gap-2">
							<input
								className={`${input} w-40 font-mono`}
								value={expression}
								onChange={(event) => setExpression(event.target.value)}
							/>
							<select
								className={`${input} w-48`}
								value={timezone}
								onChange={(event) => setTimezone(event.target.value)}
							>
								{TIMEZONES.map((tz) => (
									<option key={tz} value={tz}>
										{tz}
									</option>
								))}
							</select>
							<button
								type="button"
								disabled={busy}
								onClick={add}
								className={btn.primary}
							>
								Create
							</button>
							<button
								type="button"
								onClick={() => setAdding(false)}
								className={btn.ghost}
							>
								Cancel
							</button>
						</div>
						<p className="text-[12px] text-slate-600">
							{describeCron(expression)}
						</p>
						<ErrorBanner error={error} />
					</div>
				)}
			</div>
		</Panel>
	);
};
